import React, { useState, useEffect } from 'react';
import './Store.css';

interface Product {
  id: number;
  name: string;
  description: string;
  piPrice: number;
  originalPrice: number;
  image: string;
  category: string;
  sales: number;
  stock: number;
}

interface MerchantInfo {
  id: number;
  name: string;
  logo?: string;
  description: string;
  announcement?: string;
  business_hours?: { open: boolean };
  contact?: string;
  phone?: string;
  created_at: string;
  productCount: number;
}

interface StoreProps {
  merchantId: string;
  merchant?: any;          // ← 添加这一行
  onBack: () => void;
  axiosClient: any;
  onProductClick: (product: any) => void;
  t: (key: string) => string;
  lang: string;
}

const Store: React.FC<StoreProps> = ({ merchantId, onBack, axiosClient, onProductClick, t, lang }) => {
  const [merchant, setMerchant] = useState<MerchantInfo | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [parseUrl, setParseUrl] = useState('');
  const [parsing, setParsing] = useState(false);


  useEffect(() => {
    if (!merchantId) return;
    fetchStoreData();
  }, [merchantId]);

    const handleParseLink = async () => {
      if (!parseUrl.trim()) return;
      setParsing(true);
      try {
        const res = await axiosClient.post('/products/parse-link', { url: parseUrl.trim() });
        if (res.data?.success && res.data.data) {
          const d = res.data.data;
          alert(
            `✅ 解析成功！\n\n` +
            `名称：${d.name || '无'}\n` +
            `价格：${d.price || '无'}\n` +
            `原价：${d.originalPrice || d.price || '无'}\n` +
            `图片：${d.image ? '已缓存到平台' : '无'}\n` +
            `描述：${d.description ? d.description.substring(0, 30) + '...' : '无'}`
          );
          setParseUrl('');
        }
      } catch (e: any) {
        alert('❌ 解析失败：' + (e.response?.data?.error || '该链接暂不支持或网站拒绝访问'));
      } finally {
        setParsing(false);
      }
    };

  const fetchStoreData = async () => {
    setLoading(true);
    try {
      const merchantRes = await axiosClient.get(`/products/merchants?id=${merchantId}`);
      // 从返回的 merchants 数组中找到当前店铺
      const merchantsList = merchantRes.data?.merchants || merchantRes.data || [];
      const merchantData = Array.isArray(merchantsList) 
        ? merchantsList.find((m: any) => String(m.id) === String(merchantId))
        : merchantsList;
      
      if (!merchantData) throw new Error(t('store.merchantNotFound'));

      const productsRes = await axiosClient.get(`/products?merchant_id=${merchantId}`);
      const productsData = Array.isArray(productsRes.data) ? productsRes.data : (productsRes.data?.products || []);

      setMerchant({
        ...merchantData,
        productCount: productsData.length,
      });
      setProducts(productsData);
    } catch (err: any) {
      setError(err.message || t('store.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...Array.from(new Set(products.map(p => p.category)))];
  const filteredProducts = activeCategory === 'all'
    ? products
    : products.filter(p => p.category === activeCategory);

  const proxyImage = (url: string) => {
    if (!url) return url;
    if (url.startsWith('data:')) return url;
    return `https://img.314pi.dpdns.org/?url=${encodeURIComponent(url)}`;  // ← 去掉空格
  };

  if (loading) {
    return (
      <div className="store-loading">
        <div className="store-spinner" />
        <p>{t('store.loading')}</p>
      </div>
    );
  }

  if (error || !merchant) {
    return (
      <div className="store-error">
        <h2>😔</h2>
        <p>{error || t('store.merchantNotFound')}</p>
        <button className="store-back-btn" onClick={onBack}>{t('store.backHome')}</button>
      </div>
    );
  }

  return (
    <div className="store-page">
      {/* 顶部返回栏 */}
      <div className="store-topbar">
        <button className="store-topbar-back" onClick={onBack}>←</button>
        <span className="store-topbar-title">{merchant.name}</span>
        <span style={{width: 32}} />
      </div>
            {/* 链接解析测试 */}
      <div style={{padding:'12px 16px', background:'#f3e5f5', marginBottom:'12px'}}>
        <div style={{fontSize:'13px', fontWeight:600, color:'#611f69', marginBottom:'8px'}}>
          🔗 测试链接解析
        </div>
        <div style={{display:'flex', gap:'8px'}}>
          <input
            type="text"
            placeholder="粘贴商品链接"
            value={parseUrl}
            onChange={(e) => setParseUrl(e.target.value)}
            style={{
              flex:1,
              padding:'8px 12px',
              border:'1px solid #ddd',
              borderRadius:'6px',
              fontSize:'13px'
            }}
          />
          <button
            onClick={handleParseLink}
            disabled={parsing || !parseUrl.trim()}
            style={{
              padding:'8px 16px',
              background: parsing ? '#ccc' : '#611f69',
              color:'#fff',
              border:'none',
              borderRadius:'6px',
              cursor: parsing ? 'not-allowed' : 'pointer',
              fontSize:'13px'
            }}
          >
            {parsing ? '解析中...' : '解析'}
          </button>
        </div>
      </div>

      {/* 店铺头部 */}
      <div className="store-header">
        <div className="store-header-bg" />
        <div className="store-header-content">
          <div className="store-avatar">
            {merchant.logo ? (
              <img src={merchant.logo} alt={merchant.name} style={{width:'100%', height:'100%', objectFit:'cover'}} onError={(e: any) => e.target.style.display='none'} />
            ) : (
              merchant.name.charAt(0).toUpperCase()
            )}
          </div>
          <div className="store-info">
            <h1 className="store-name">{merchant.name}</h1>
            <p className="store-desc">{merchant.description}</p>
            <div className="store-meta">
              <span className="store-meta-item">
                📦 {merchant.productCount} {t('store.products')}
              </span>
              <span className="store-meta-item">
                🕐 {t('store.joined')} {new Date(merchant.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 营业状态 */}
      {merchant.business_hours && (
        <div className="store-meta" style={{marginTop:'8px'}}>
          <span className="store-meta-item" style={{
            color: merchant.business_hours.open ? '#4caf50' : '#f44336',
            fontWeight: 'bold'
          }}>
            {merchant.business_hours.open ? '🟢 ' + t('store.open') : '🔴 ' + t('store.closed')}
          </span>
        </div>
      )}

      {/* 店铺公告 */}
      {merchant.announcement && (
        <div style={{
          marginTop:'12px',
          padding:'12px 16px',
          background: '#fff3e0',
          borderRadius: '8px',
          borderLeft: '4px solid #ff9800'
        }}>
          <div style={{fontSize:'12px', color:'#e65100', fontWeight:'bold', marginBottom:'4px'}}>📢 {t('store.announcement')}</div>
          <div style={{fontSize:'13px', color:'#333'}}>{merchant.announcement}</div>
        </div>
      )}
      
      {/* 分类标签 */}
      <div className="store-categories">
        {categories.map(cat => (
          <button
            key={cat}
            className={`store-cat-btn ${activeCategory === cat ? 'active' : ''}`}
            onClick={() => setActiveCategory(cat)}
          >
            {cat === 'all' ? t('store.allCategories') : cat}
          </button>
        ))}
      </div>
  
      {/* 商品列表 */}
      <div className="store-products">
        {filteredProducts.length === 0 ? (
          <div className="store-empty">
            <p>📭 {t('store.noProducts')}</p>
          </div>
        ) : (
          <div className="store-products-grid">
            {filteredProducts.map(product => (
              <div
                key={product.id}
                className="store-product-card"
                onClick={() => onProductClick({
                  id: String(product.id),
                  name: product.name,
                  image: product.image,
                  originalPrice: product.originalPrice,
                  piPrice: product.piPrice,
                  category: product.category,
                  description: product.description,
                  sales: product.sales,
                })}
              >
                <div className="store-product-img">
                  <img src={product.image + '?w=400&q=75&format=auto'} alt={product.name} loading="lazy" />
                </div>
                <div className="store-product-info">
                  <h3 className="store-product-name">{product.name}</h3>
                  <p className="store-product-desc">{product.description}</p>
                  <div className="store-product-price">
                    <span className="store-pi-price">π {product.piPrice}</span>
                    {product.originalPrice > 0 && (
                      <span className="store-original-price">π {product.originalPrice}</span>
                    )}
                  </div>
                  <div className="store-product-stats">
                    <span>🔥 {product.sales} {t('store.sold')}</span>
                    <span>📦 {product.stock}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Store;
